import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  InputAdornment,
  Link,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import InboxRoundedIcon from "@mui/icons-material/InboxRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import { Link as RouterLink } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { usePlatform } from "../platform/store";
import {
  DataTable,
  InlineActions,
  PageRoot,
  PrimaryAction,
  RecordCard,
  SectionCard,
  SecondaryAction,
  StatusChip,
  SummaryGrid,
} from "./ui/PlatformPrimitives";

const tabConfig = [
  { key: "sources", label: "Sources" },
  { key: "feed", label: "Feed" },
  { key: "changes", label: "Changes" },
  { key: "impacts", label: "Impacts" },
  { key: "ingredients", label: "Ingredients" },
];

const LAST_VISIT_KEY = "kodo.regulatoryIntelligence.lastVisitedAt";
const readLastVisit = () => {
  try {
    return localStorage.getItem(LAST_VISIT_KEY) || "";
  } catch {
    return "";
  }
};
const writeLastVisit = () => {
  try {
    localStorage.setItem(LAST_VISIT_KEY, new Date().toISOString());
  } catch {
    /* ignore */
  }
};

const isAfter = (iso, sinceIso) => {
  if (!iso || !sinceIso) return false;
  const t1 = Date.parse(iso);
  const t2 = Date.parse(sinceIso);
  return Number.isFinite(t1) && Number.isFinite(t2) && t1 > t2;
};

const severityRank = (level) => {
  const v = String(level || "").toLowerCase();
  if (v === "critical" || v === "high") return 3;
  if (v === "medium" || v === "error") return 2;
  if (v === "warning" || v === "low") return 1;
  return 0;
};

const EmptyState = ({ icon, title, hint, action }) => {
  const theme = useTheme();
  return (
    <Box sx={{ py: 6, textAlign: "center" }}>
      <Box
        sx={{
          width: 48, height: 48, borderRadius: "50%",
          // Glass-inset blob (theme tier) behind the empty-state icon so it
          // sits on the page gradient rather than being a flat tan disc.
          background: theme.glass.inset.background,
          backdropFilter: theme.glass.inset.backdropFilter,
          WebkitBackdropFilter: theme.glass.inset.WebkitBackdropFilter,
          border: theme.glass.inset.border,
          display: "grid", placeItems: "center", mx: "auto", mb: 2,
        }}
      >
        {icon || <InboxRoundedIcon sx={{ fontSize: 22, color: "text.disabled" }} />}
      </Box>
      <Typography variant="body1" sx={{ color: "text.primary", fontWeight: 500, mb: 0.5 }}>{title}</Typography>
      {hint && <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: action ? 2 : 0 }}>{hint}</Typography>}
      {action}
    </Box>
  );
};

const downloadJson = (filename, payload) => {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};

const RegulatoryIntelligence = () => {
  const theme = useTheme();
  const {
    regulatoryUpdates,
    regulatorySources,
    regulatoryChanges,
    regulatoryTracker,
    ingredientExplorer,
    runRegulatorySync,
    runAllRegulatorySyncs,
    refreshRegulatoryImpacts,
  } = usePlatform();
  const [tab, setTab] = useState("sources");
  const [sourceFilter, setSourceFilter] = useState("");
  const [ingredientQuery, setIngredientQuery] = useState("");
  const [changeSort, setChangeSort] = useState("severity");
  const [changeNewOnly, setChangeNewOnly] = useState(false);
  const [impactFilter, setImpactFilter] = useState("all");
  const [errorMessage, setErrorMessage] = useState("");
  const [syncingAll, setSyncingAll] = useState(false);
  const [syncProgress, setSyncProgress] = useState({ total: 0, done: 0, current: "" });
  const [syncBanner, setSyncBanner] = useState("");
  const [recomputing, setRecomputing] = useState(false);
  const [syncingOne, setSyncingOne] = useState("");
  const [lastVisit] = useState(readLastVisit);

  useEffect(() => {
    writeLastVisit();
  }, []);

  const annexChangeCount = useMemo(
    () => regulatoryChanges.filter((change) => change.entityType === "eu_annex_rule").length,
    [regulatoryChanges]
  );

  const criticalImpacts = useMemo(
    () => regulatoryTracker.impacts.filter((impact) => impact.status === "Blocked").length,
    [regulatoryTracker.impacts]
  );

  const healthySourceCount = regulatoryTracker.sourceHealth.filter((source) => source.status === "Healthy").length;
  const failingSources = regulatoryTracker.sourceHealth.filter((source) => source.status === "Blocked" || source.status === "Error");

  // ── Search + filter state ────────────────────────────────────────────────
  const visibleIngredients = useMemo(() => {
    const query = ingredientQuery.trim().toLowerCase();
    if (!query) return ingredientExplorer;
    return ingredientExplorer.filter((row) => {
      const hay = [row.name, row.inciName, row.casNumber, ...(row.casNumbers || []), ...(row.synonyms || [])]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(query);
    });
  }, [ingredientQuery, ingredientExplorer]);

  const visibleChanges = useMemo(() => {
    let rows = regulatoryChanges.slice();
    if (sourceFilter) rows = rows.filter((change) => change.sourceType === sourceFilter);
    if (changeNewOnly && lastVisit) {
      rows = rows.filter((change) => isAfter(change.detectedAt, lastVisit));
    }
    if (changeSort === "severity") {
      rows.sort((a, b) => severityRank(b.impactLevel) - severityRank(a.impactLevel));
    } else if (changeSort === "newest") {
      rows.sort((a, b) => String(b.detectedAt || "").localeCompare(String(a.detectedAt || "")));
    }
    return rows;
  }, [regulatoryChanges, sourceFilter, changeNewOnly, lastVisit, changeSort]);

  const visibleImpacts = useMemo(() => {
    let rows = regulatoryTracker.impacts.slice();
    if (impactFilter !== "all") {
      rows = rows.filter((impact) => String(impact.status).toLowerCase() === impactFilter);
    }
    rows.sort((a, b) => {
      const rank = { blocked: 0, review: 1, clear: 2 };
      const ra = rank[String(a.status).toLowerCase()] ?? 9;
      const rb = rank[String(b.status).toLowerCase()] ?? 9;
      if (ra !== rb) return ra - rb;
      return String(a.productName).localeCompare(String(b.productName));
    });
    return rows;
  }, [regulatoryTracker.impacts, impactFilter]);

  // ── Actions ──────────────────────────────────────────────────────────────
  const handleSync = async (sourceType) => {
    try {
      setErrorMessage("");
      setSyncingOne(sourceType);
      await runRegulatorySync(sourceType);
    } catch (error) {
      setErrorMessage(error.message || "Unable to sync regulatory source.");
    } finally {
      setSyncingOne("");
    }
  };

  const handleSyncAll = async () => {
    try {
      setErrorMessage("");
      setSyncingAll(true);
      setSyncBanner("");
      const sources = regulatorySources.slice();
      setSyncProgress({ total: sources.length, done: 0, current: sources[0]?.label || "" });
      const startingChangeCount = regulatoryChanges.length;
      for (let i = 0; i < sources.length; i += 1) {
        const source = sources[i];
        setSyncProgress({ total: sources.length, done: i, current: source.label });
        try {
          await runRegulatorySync(source.sourceType);
        } catch (err) {
          // continue; per-source errors are captured in source status
        }
      }
      setSyncProgress({ total: sources.length, done: sources.length, current: "" });
      const newChangeCount = Math.max(0, regulatoryChanges.length - startingChangeCount);
      setSyncBanner(
        newChangeCount > 0
          ? `Sync complete. ${newChangeCount} new regulatory change${newChangeCount === 1 ? "" : "s"} detected.`
          : `Sync complete. No new regulatory changes detected.`
      );
    } catch (error) {
      setErrorMessage(error.message || "Unable to sync all sources.");
    } finally {
      setSyncingAll(false);
    }
  };

  const handleRetryFailed = async () => {
    try {
      setErrorMessage("");
      setSyncingAll(true);
      for (const source of failingSources) {
        setSyncProgress({ total: failingSources.length, done: 0, current: source.label });
        try {
          await runRegulatorySync(source.sourceType);
        } catch (err) {
          /* continue */
        }
      }
      setSyncProgress({ total: 0, done: 0, current: "" });
    } finally {
      setSyncingAll(false);
    }
  };

  const handleRecompute = async () => {
    try {
      setErrorMessage("");
      setRecomputing(true);
      await refreshRegulatoryImpacts();
    } catch (error) {
      setErrorMessage(error.message || "Unable to refresh impacts.");
    } finally {
      setRecomputing(false);
    }
  };

  const handleExportAudit = () => {
    downloadJson(`regulatory-audit-${new Date().toISOString().slice(0, 10)}.json`, {
      exportedAt: new Date().toISOString(),
      sources: regulatoryTracker.sourceHealth,
      recentUpdates: regulatoryUpdates,
      changes: regulatoryChanges,
      impacts: regulatoryTracker.impacts,
      ingredientIndexSize: ingredientExplorer.length,
    });
  };

  const progressLabel = syncingAll && syncProgress.total > 0
    ? `Syncing ${syncProgress.current || "…"} (${syncProgress.done + 1}/${syncProgress.total})`
    : null;

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <PageRoot>
      <SummaryGrid
        items={[
          ["Sources", regulatorySources.length, `${healthySourceCount} healthy`],
          ["Ingredients indexed", ingredientExplorer.length, "IFRA + EU Annex + allergens + PFAS"],
          ["EU Annex Changes", annexChangeCount, "Structured diff records"],
          ["Blocked Impacts", criticalImpacts, `${regulatoryTracker.impacts.length} portfolio matches`],
        ]}
      />

      {errorMessage ? <Alert severity="error" onClose={() => setErrorMessage("")}>{errorMessage}</Alert> : null}
      {syncBanner ? <Alert severity="success" onClose={() => setSyncBanner("")}>{syncBanner}</Alert> : null}

      <SectionCard
        title="Regulatory Intelligence"
        action={
          <InlineActions>
            <Button
              variant="contained"
              size="small"
              startIcon={syncingAll ? <CircularProgress size={14} sx={{ color: "primary.contrastText" }} /> : <SyncRoundedIcon sx={{ fontSize: 16 }} />}
              onClick={handleSyncAll}
              disabled={syncingAll}
            >
              {syncingAll ? progressLabel || "Syncing…" : "Sync all"}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={recomputing ? <CircularProgress size={14} /> : <RefreshRoundedIcon sx={{ fontSize: 16 }} />}
              onClick={handleRecompute}
              disabled={recomputing}
            >
              {recomputing ? "Computing…" : "Recompute impacts"}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadRoundedIcon sx={{ fontSize: 16 }} />}
              onClick={handleExportAudit}
            >
              Audit
            </Button>
          </InlineActions>
        }
      >
        <Collapse in={syncingAll && syncProgress.total > 0}>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ height: 4, bgcolor: "rgba(0,0,0,0.06)", borderRadius: 999, overflow: "hidden", mb: 1 }}>
              <Box
                sx={{
                  height: "100%",
                  width: `${(syncProgress.done / Math.max(1, syncProgress.total)) * 100}%`,
                  bgcolor: "primary.main",
                  borderRadius: 999,
                  transition: `width ${theme.motion.morph}`,
                }}
              />
            </Box>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {syncProgress.current ? `Syncing ${syncProgress.current}` : "Finishing up…"}
            </Typography>
          </Box>
        </Collapse>

        <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable" allowScrollButtonsMobile>
          {tabConfig.map((item) => (
            <Tab key={item.key} label={item.label} value={item.key} />
          ))}
        </Tabs>
      </SectionCard>

      {/* ── Sources ─────────────────────────────────────────────────────── */}
      {tab === "sources" ? (
        <SectionCard
          title="Source health"
          action={
            failingSources.length > 0 ? (
              <Button
                size="small"
                variant="outlined"
                startIcon={<RefreshRoundedIcon sx={{ fontSize: 14 }} />}
                onClick={handleRetryFailed}
                disabled={syncingAll}
              >
                Retry {failingSources.length} failed
              </Button>
            ) : null
          }
        >
          {regulatoryTracker.sourceHealth.length === 0 ? (
            <EmptyState title="No sources configured" hint="Regulatory sources are set up via seed data." />
          ) : (
            <Grid container spacing={2}>
              {regulatoryTracker.sourceHealth.map((source) => {
                const isSyncing = syncingOne === source.sourceType || syncingAll;
                const neverSynced = !source.lastSyncedAt;
                return (
                  <Grid key={source.id || source.sourceType} size={{ xs: 12, md: 6 }}>
                    <RecordCard
                      title={source.label}
                      subtitle={source.description}
                      status={source.status}
                      chips={[source.trustLevel?.replaceAll("_", " "), `${source.recordCount} records`].filter(Boolean)}
                      footer={
                        <Stack spacing={1}>
                          <Typography variant="caption" color="text.secondary">
                            {neverSynced ? "Never synced" : `Last sync: ${new Date(source.lastSyncedAt).toLocaleString()}`}
                          </Typography>
                          {source.lastSyncError ? (
                            <Typography variant="caption" color="error">{source.lastSyncError}</Typography>
                          ) : null}
                          {source.lastLiveSummary ? (
                            <Typography variant="caption" color="text.secondary" sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                              {source.lastLiveSummary}
                            </Typography>
                          ) : null}
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleSync(source.sourceType)}
                              disabled={isSyncing}
                              startIcon={isSyncing ? <CircularProgress size={14} sx={{ color: "primary.contrastText" }} /> : <SyncRoundedIcon sx={{ fontSize: 14 }} />}
                            >
                              {isSyncing ? "Syncing…" : "Sync"}
                            </Button>
                            {source.sourceUrl ? (
                              <Link href={source.sourceUrl} target="_blank" rel="noreferrer" underline="hover" sx={{ fontSize: "0.75rem", display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                                Open source <OpenInNewRoundedIcon sx={{ fontSize: 12 }} />
                              </Link>
                            ) : null}
                          </Stack>
                        </Stack>
                      }
                    />
                  </Grid>
                );
              })}
            </Grid>
          )}
        </SectionCard>
      ) : null}

      {/* ── Feed ────────────────────────────────────────────────────────── */}
      {tab === "feed" ? (
        <SectionCard title="Latest regulatory records">
          {regulatoryUpdates.length === 0 ? (
            <EmptyState
              title="No records yet"
              hint="Sync sources to fetch live regulatory updates."
              action={<SecondaryAction size="small" onClick={handleSyncAll} disabled={syncingAll}>{syncingAll ? "Syncing…" : "Sync all now"}</SecondaryAction>}
            />
          ) : (
            <Grid container spacing={2}>
              {regulatoryUpdates.map((update) => (
                <Grid key={update.id} size={{ xs: 12, md: 6 }}>
                  <RecordCard
                    title={update.title}
                    subtitle={update.summary}
                    status={update.jurisdiction}
                    chips={[update.category, update.source, update.effectiveDate].filter(Boolean)}
                    footer={
                      <Stack spacing={0.5}>
                        {update.impact && <Typography variant="caption" color="text.secondary">{update.impact}</Typography>}
                        {update.sourceUrl ? (
                          <Link href={update.sourceUrl} target="_blank" rel="noreferrer" underline="hover" sx={{ fontSize: "0.75rem", display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                            Open source <OpenInNewRoundedIcon sx={{ fontSize: 12 }} />
                          </Link>
                        ) : null}
                      </Stack>
                    }
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </SectionCard>
      ) : null}

      {/* ── Changes ─────────────────────────────────────────────────────── */}
      {tab === "changes" ? (
        <>
          {regulatoryChanges.length > 0 && (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <TextField
                size="small"
                select
                label="Source"
                value={sourceFilter}
                onChange={(event) => setSourceFilter(event.target.value)}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="">All sources</MenuItem>
                {Array.from(new Set(regulatorySources.map((s) => s.sourceType))).map((st) => (
                  <MenuItem key={st} value={st}>{st}</MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                select
                label="Sort"
                value={changeSort}
                onChange={(event) => setChangeSort(event.target.value)}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="severity">Severity</MenuItem>
                <MenuItem value="newest">Newest</MenuItem>
              </TextField>
              <Chip
                label={changeNewOnly ? "New since last visit ✓" : "New since last visit"}
                size="small"
                onClick={() => setChangeNewOnly((v) => !v)}
                sx={{
                  cursor: "pointer",
                  borderRadius: 999,
                  transition: theme.motion.tint,
                  ...(changeNewOnly
                    ? { bgcolor: "primary.main", color: "primary.contrastText", border: "1px solid rgba(255,255,255,0.20)" }
                    : {}),
                }}
              />
            </Stack>
          )}

          <SectionCard title={`Detected changes · ${visibleChanges.length}`}>
            {regulatoryChanges.length === 0 ? (
              <EmptyState
                title="No changes detected"
                hint="Sync sources to detect regulatory updates since the last snapshot."
                action={<SecondaryAction size="small" onClick={handleSyncAll} disabled={syncingAll}>{syncingAll ? "Syncing…" : "Sync all now"}</SecondaryAction>}
              />
            ) : visibleChanges.length === 0 ? (
              <EmptyState title="No changes match your filter" hint="Clear the filter or toggle off 'new since last visit'." />
            ) : (
              <DataTable
                columns={[
                  { key: "changeType", label: "Change" },
                  { key: "entityKey", label: "Key" },
                  { key: "impactLevel", label: "Severity", render: (row) => <StatusChip status={row.impactLevel} /> },
                  { key: "oldValue", label: "Old", render: (row) => row.oldValue || "—" },
                  { key: "newValue", label: "New", render: (row) => row.newValue || "—" },
                  { key: "sourceType", label: "Source" },
                  {
                    key: "summary",
                    label: "Evidence",
                    render: (row) => (
                      <Stack spacing={0.5}>
                        <Typography variant="body2">{row.summary}</Typography>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          {row.sourceUrl && (
                            <Link href={row.sourceUrl} target="_blank" rel="noreferrer" underline="hover" sx={{ fontSize: "0.75rem", display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                              Open source <OpenInNewRoundedIcon sx={{ fontSize: 11 }} />
                            </Link>
                          )}
                          {lastVisit && isAfter(row.detectedAt, lastVisit) && (
                            <Chip
                              label="new"
                              size="small"
                              sx={{
                                height: 20,
                                fontWeight: 600,
                                borderRadius: 999,
                                // Translucent warning tint over glass — palette
                                // token resolves to rgba(146,64,14,0.14).
                                bgcolor: "warning.light",
                                color: "warning.main",
                                border: "1px solid rgba(146,64,14,0.22)",
                                "& .MuiChip-label": { fontSize: "0.75rem" },
                              }}
                            />
                          )}
                        </Stack>
                      </Stack>
                    ),
                  },
                ]}
                rows={visibleChanges}
                getRowKey={(row) => row.id}
              />
            )}
          </SectionCard>
        </>
      ) : null}

      {/* ── Impacts ─────────────────────────────────────────────────────── */}
      {tab === "impacts" ? (
        <>
          {regulatoryTracker.impacts.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
              {["all", "blocked", "review", "clear"].map((key) => (
                <Chip
                  key={key}
                  label={`${key.charAt(0).toUpperCase() + key.slice(1)} (${
                    key === "all" ? regulatoryTracker.impacts.length
                    : regulatoryTracker.impacts.filter((i) => String(i.status).toLowerCase() === key).length
                  })`}
                  size="small"
                  onClick={() => setImpactFilter(key)}
                  sx={{
                    cursor: "pointer",
                    fontWeight: 500,
                    borderRadius: 999,
                    transition: theme.motion.tint,
                    ...(impactFilter === key
                      ? { bgcolor: "primary.main", color: "primary.contrastText", border: "1px solid rgba(255,255,255,0.20)" }
                      : {}),
                  }}
                />
              ))}
            </Stack>
          )}
          <SectionCard title={`Portfolio impacts · ${visibleImpacts.length}`}>
            {regulatoryTracker.impacts.length === 0 ? (
              <EmptyState
                title="No impacts detected"
                hint="Portfolio impacts appear when regulatory changes match ingredients in saved formulas."
                action={
                  <Stack direction="row" spacing={1} justifyContent="center">
                    <SecondaryAction size="small" onClick={handleSyncAll} disabled={syncingAll}>{syncingAll ? "Syncing…" : "Sync all"}</SecondaryAction>
                    <SecondaryAction size="small" onClick={handleRecompute} disabled={recomputing}>{recomputing ? "Computing…" : "Recompute"}</SecondaryAction>
                  </Stack>
                }
              />
            ) : visibleImpacts.length === 0 ? (
              <EmptyState title="No impacts match this filter" hint="Try a different status filter." />
            ) : (
              <DataTable
                columns={[
                  {
                    key: "productName",
                    label: "Product",
                    render: (row) => (
                      <Link component={RouterLink} to={`/perfume-dashboard?product=${row.productId}`} underline="hover" sx={{ color: "primary.main", fontWeight: 600 }}>
                        {row.productName}
                      </Link>
                    ),
                  },
                  { key: "ingredientName", label: "Ingredient" },
                  { key: "impactLevel", label: "Level", render: (row) => <StatusChip status={row.impactLevel} /> },
                  {
                    key: "formulaPercentage",
                    label: "Formula %",
                    render: (row) => (Number.isFinite(row.formulaPercentage) ? `${row.formulaPercentage}%` : "Unverified"),
                  },
                  {
                    key: "ruleDelta",
                    label: "Rule delta",
                    render: (row) =>
                      row.oldValue || row.newValue ? `${row.oldValue || "—"} → ${row.newValue || "—"}` : row.changeType,
                  },
                  { key: "action", label: "Required action" },
                  { key: "status", label: "Status", render: (row) => <StatusChip status={row.status} /> },
                  {
                    key: "fix",
                    label: "",
                    render: (row) => (
                      <Button
                        size="small"
                        variant="outlined"
                        component={RouterLink}
                        to={`/perfume-dashboard?product=${row.productId}`}
                        sx={{ borderRadius: 999, minWidth: 0, px: 2 }}
                      >
                        Fix
                      </Button>
                    ),
                  },
                ]}
                rows={visibleImpacts}
                getRowKey={(row) => row.id}
              />
            )}
          </SectionCard>
        </>
      ) : null}

      {/* ── Ingredients (unified explorer) ──────────────────────────────── */}
      {tab === "ingredients" ? (
        <>
          <SectionCard title={`Ingredient explorer · ${ingredientExplorer.length} indexed`}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by INCI name, CAS, synonym…"
              value={ingredientQuery}
              onChange={(event) => setIngredientQuery(event.target.value)}
              slotProps={{
                input: {
                  startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 16, color: "text.disabled" }} /></InputAdornment>,
                },
              }}
            />
          </SectionCard>

          <SectionCard title={`Matches · ${visibleIngredients.length}`}>
            {ingredientExplorer.length === 0 ? (
              <EmptyState title="Ingredient index empty" hint="Sync EUR-Lex to load the EU Annex snapshot." />
            ) : visibleIngredients.length === 0 ? (
              <EmptyState title="No ingredients match" hint="Try a CAS number or a different name." />
            ) : (
              <Stack spacing={1}>
                {visibleIngredients.slice(0, 60).map((row) => (
                  <IngredientRow key={row.id} row={row} />
                ))}
                {visibleIngredients.length > 60 && (
                  <Typography variant="caption" sx={{ color: "text.disabled", textAlign: "center", mt: 2, display: "block" }}>
                    Showing first 60 of {visibleIngredients.length}. Refine your search to see more.
                  </Typography>
                )}
              </Stack>
            )}
          </SectionCard>
        </>
      ) : null}
    </PageRoot>
  );
};

// Translucent severity palettes (Liquid Glass: 14% fill / 22% border, layered
// over the row's glass-inset surface — the wet rim still reads beneath).
// Color hex values are the allowlist contrast-text trio + brand navy.
const severityBg = {
  prohibited: { bg: "rgba(153,27,27,0.14)", color: "#991B1B", border: "rgba(153,27,27,0.22)" },
  restricted: { bg: "rgba(146,64,14,0.14)", color: "#92400E", border: "rgba(146,64,14,0.22)" },
  labeling:   { bg: "rgba(27,42,74,0.10)",  color: "#1B2A4A", border: "rgba(27,42,74,0.18)" },
  reporting:  { bg: "rgba(27,42,74,0.10)",  color: "#1B2A4A", border: "rgba(27,42,74,0.18)" },
  testing:    { bg: "rgba(27,42,74,0.10)",  color: "#1B2A4A", border: "rgba(27,42,74,0.18)" },
  listed:     { bg: "rgba(0,0,0,0.06)",     color: "rgba(0,0,0,0.60)", border: "rgba(0,0,0,0.10)" },
};

const IngredientRow = ({ row }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  // Severity contrast text — hex values are in the allowlist (severity trio +
  // brand navy). Fallback resolves to text.primary via palette token below.
  const headlineColor = {
    Prohibited: "#991B1B",
    Restricted: "#92400E",
    Labeling: "#1B2A4A",
    Reporting: "#1B2A4A",
    Listed: theme.palette.text.secondary,
  }[row.trustLevel] || theme.palette.text.primary;
  return (
    // Glass inset row — sits on the page gradient like a nested Paper.
    <Box
      sx={{
        background: theme.glass.inset.background,
        backdropFilter: theme.glass.inset.backdropFilter,
        WebkitBackdropFilter: theme.glass.inset.WebkitBackdropFilter,
        border: theme.glass.inset.border,
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.45)",
        borderRadius: "12px",
        transition: `background ${theme.motion.tint}`,
        "&:hover": { background: "rgba(255,255,255,0.42)" },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{ px: 2, py: 1.5, cursor: "pointer", minHeight: 36 }}
        onClick={() => setExpanded((v) => !v)}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 500, color: "text.primary" }}>
            {row.name}
            {row.casNumber && (
              <Typography component="span" variant="caption" sx={{ color: "text.disabled", ml: 1, fontFamily: "monospace" }}>
                CAS {row.casNumber}
              </Typography>
            )}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", justifyContent: "flex-end", maxWidth: "60%", gap: 0.5 }}>
          {row.flags.slice(0, 4).map((flag) => {
            const meta = severityBg[flag.severity] || severityBg.listed;
            return (
              <Chip
                key={flag.code}
                label={flag.label}
                size="small"
                sx={{
                  bgcolor: meta.bg,
                  color: meta.color,
                  border: `1px solid ${meta.border}`,
                  borderRadius: 999,
                  fontWeight: 600,
                }}
              />
            );
          })}
        </Stack>
        <Typography variant="caption" sx={{ color: headlineColor, fontWeight: 600, minWidth: 72, textAlign: "right" }}>
          {row.trustLevel}
        </Typography>
      </Stack>
      <Collapse in={expanded}>
        <Box
          sx={{
            px: 2.5, py: 2,
            // Hairline divider (translucent black) above the inset panel.
            borderTop: "1px solid rgba(0,0,0,0.06)",
            background: theme.glass.inset.background,
            borderBottomLeftRadius: 12,
            borderBottomRightRadius: 12,
          }}
        >
          <Stack spacing={1.5}>
            {row.casNumbers && row.casNumbers.length > 1 && (
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                <strong>All CAS:</strong> {row.casNumbers.join(", ")}
              </Typography>
            )}
            {row.synonyms && row.synonyms.length > 0 && (
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                <strong>Synonyms:</strong> {row.synonyms.join(" · ")}
              </Typography>
            )}
            <Stack spacing={1}>
              {row.flags.map((flag) => (
                // Gold accent rail uses the brand gold release (#7A631D) at
                // 55% to preserve the translucent vibrancy over the glass row.
                <Box key={flag.code} sx={{ borderLeft: "3px solid rgba(122,99,29,0.55)", pl: 1.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: "text.primary", display: "block" }}>
                    {flag.label}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.primary" }}>
                    {flag.detail}
                  </Typography>
                </Box>
              ))}
            </Stack>
            {row.sources.length > 0 && (
              <Stack direction="row" spacing={1.5} flexWrap="wrap">
                {row.sources.map((source) => (
                  <Link
                    key={`${source.kind}-${source.url}`}
                    href={source.url}
                    target="_blank"
                    rel="noreferrer"
                    underline="hover"
                    sx={{ fontSize: "0.75rem", display: "inline-flex", alignItems: "center", gap: 0.5, color: "primary.main" }}
                  >
                    {source.label} <OpenInNewRoundedIcon sx={{ fontSize: 12 }} />
                  </Link>
                ))}
              </Stack>
            )}
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
};

export default RegulatoryIntelligence;
